import { isValidElement, useCallback, useMemo, useState, type Key, type ReactNode } from "react";
import type { TableColumnGroupType as ColumnGroupType, TableColumnType as ColumnType, TableProps } from "../ui";
import { SettingsListPage, type ProColumnsWithMeasure, type SettingsListPageProps } from "../settings/SettingsListPage";
import { useResolvedSettingsListRuntime } from "../settings/settingsListRuntimeContext";
import { sharedSettingsListDe, sharedSettingsListEn } from "../settings/settingsListTranslations";
import { expandSettingsListRenderers } from "../settings/settingsListElementRoles";
import type { ListActionDefinition } from "../settings/listActions";
import type { ColumnDefinition } from "../settings/useProTableColumnEditor";
import type { LocalizedText, SettingsListRuntime } from "../settings/settingsListTypes";
import { useUiLanguage } from "../ui/uiText";
import type { ManagedTableColumn } from "./ManagedTableColumns";
import { LANGUAGE_DE } from "../i18n/languageCodes";

export interface ManagedTableListProps<T extends object> {
  /** Persistenz-Schlüssel für Spaltenkonfiguration, Sortierung und Seitengröße. */
  storageKey: string;
  /** Verwaltete Spalten oder Antd-Spalten; fehlende Schlüssel werden aus `dataIndex` bzw. der Position abgeleitet. */
  columns: readonly ManagedTableListColumn<T>[];
  dataSource: readonly T[];
  tr: (key: string) => string;
  rowKey?: TableProps<T>["rowKey"];
  loading?: boolean;
  /** `false` = alle Einträge auf einer Seite; sonst `pageSize` (Standard 10). Mit `current` + `total` blättert der Aufrufer. */
  pagination?: TableProps<T>["pagination"];
  /** Status unter der Liste, auch ohne Seitenblättern. */
  paginationSummary?: SettingsListPageProps<T>["paginationSummary"];
  /**
   * Pflicht bei gesteuertem Blättern (`current` + `total`): Die Liste sortiert die Seite nicht selbst, sondern
   * meldet die aktive Sortierung (beim Einhängen und bei jeder Änderung); der Aufrufer lädt sortiert vom Server.
   * `field` ist `dataIndex` (mit Punkten) bzw. ohne ihn der Spaltenschlüssel.
   */
  onSortChange?: (sort: ManagedTableSort | null) => void;
  emptyText?: ReactNode;
  /** Ausgewertet: `expandedRowRender`, `rowExpandable`, gesteuert `expandedRowKeys`. */
  expandable?: TableProps<T>["expandable"];
  /** Stille Ausgangsreihenfolge (Systemsortierung) ohne Spaltenpfeil; manuelle Klicks werden gespeichert. */
  defaultSort?: { field: string; order: "ascend" | "descend" };
  /** Ausgewertet werden `rowSelection`, `onRow` (Klick; ein Doppelklick gilt als Klick) und `rowClassName`; übrige Tabellen-Eigenschaften entfallen. */
  tableProps?: Partial<TableProps<T>>;
  /** Hervorgehobene Zeile (z. B. die im Detailbereich gezeigte Auswahl). */
  activeRowKey?: Key | null;
  /** Manuelle Zeilenreihenfolge per Ziehgriff; `onReorder` erhält die Zeilenschlüssel in neuer Reihenfolge. */
  rowReorder?: SettingsListPageProps<T>["rowReorder"];
  rowDrag?: SettingsListPageProps<T>["rowDrag"];
  /** Zeilen-Overlay statt der Datenzellen, z. B. Live-Fortschritt eines laufenden Vorgangs. */
  rowOverlay?: SettingsListPageProps<T>["rowOverlay"];
  /** Summenzeile unter der Liste: Wert je Spaltenschlüssel, berechnet über alle Daten in Anzeigereihenfolge. */
  summary?: (data: readonly T[]) => Partial<Record<string, ReactNode>>;
  /** Strukturierte Zeilenaktionen (Symbol plus Text, Nur-Symbol bei wenig Platz); ersetzen eine Aktionsspalte. */
  actions?: ListActionDefinition<T>[];
  /** Schlüssel der Aktionsspalte unter `columns` (Standard `actions`; `action` und `pinned: "right"` gelten ebenso). */
  actionColumnKey?: string;
  className?: string;
  /** Filter links in der gemeinsamen, themegestalteten Werkzeugleiste. Kopfaktionen bleiben rechts. */
  toolbarExtra?: ReactNode;
  /** @deprecated Ohne Wirkung: die gemeinsame Liste hat eine Dichte. */
  size?: TableProps<T>["size"];
  /** @deprecated Ohne Wirkung: der Rahmen kommt aus dem Kit. */
  bordered?: boolean;
  /** @deprecated Ohne Wirkung: die gemeinsame Liste hat keinen horizontalen Scrollbalken. */
  scrollX?: number | false;
}

/** Aktive Sortierung einer serverseitig sortierten Liste. */
export type ManagedTableSort = { field: string; order: "ascend" | "descend" };

/** Spaltenkonfiguration, die ein Produkt-Hook für {@link ManagedTableList} vorbereitet. */
export type ManagedTableListConfig<T extends object> = Pick<ManagedTableListProps<T>, "storageKey" | "columns" | "tr" | "defaultSort">;

export type ManagedTableListColumn<T> = ManagedTableColumn<T> | ColumnType<T> | ColumnGroupType<T>;

/** Rahmen der verwalteten Liste; Titel kennzeichnen ausschließlich fachliche Abschnitte. */
export interface ManagedTableFrame {
  sectionTitle?: ReactNode;
  headerExtra?: ReactNode;
  beforeTable?: ReactNode;
  footer?: ReactNode;
  /** Ohne eigene Karte: der Aufrufer liefert den gepolsterten Rahmen. */
  embedded: boolean;
}

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_ACTION_COLUMN_KEY = "actions";
const ROW_ACTION_COLUMN_KEY = "action";
const SORT_ASCEND: ManagedTableSort["order"] = "ascend";
const SORT_DESCEND: ManagedTableSort["order"] = "descend";

/** Spaltengruppen werden aufgelöst; Spalten ohne `key` bekommen einen stabilen Schlüssel. */
function withKeys<T>(columns: readonly ManagedTableListColumn<T>[], prefix = ""): ManagedTableColumn<T>[] {
  return columns.flatMap((column, index) => {
    if ("children" in column && column.children) return withKeys(column.children, `${prefix}${index}.`);
    const plain = column as ColumnType<T>;
    const dataKey = Array.isArray(plain.dataIndex) ? plain.dataIndex.join(".") : plain.dataIndex != null ? String(plain.dataIndex) : null;
    return [{ ...plain, key: plain.key != null ? String(plain.key) : dataKey ?? `column-${prefix}${index}` } as ManagedTableColumn<T>];
  });
}

function sortFieldOf<T>(column: ManagedTableColumn<T>): string {
  return Array.isArray(column.dataIndex) ? column.dataIndex.join(".") : String(column.dataIndex ?? column.key);
}

function valueAt<T extends object>(record: T, dataIndex: ManagedTableColumn<T>["dataIndex"]): unknown {
  const path = Array.isArray(dataIndex) ? dataIndex : dataIndex == null ? [] : [dataIndex];
  return path.reduce<unknown>((value, part) => value != null && typeof value === "object" ? (value as Record<string | number, unknown>)[String(part)] : undefined, record);
}

function readStorage(key: string): string | null {
  try { return window.localStorage.getItem(key); } catch { return null; }
}

/**
 * Einmalige Übernahme der früheren Einstellungen der verwalteten Liste (`managedTable.*`) in die Schlüssel
 * der gemeinsamen Liste; danach werden die Altschlüssel gelöscht. Spaltenformat ist gleich; eine leere
 * Sortierung (`null`) bedeutet in beiden „keine manuelle Sortierung“ und wird nicht übertragen.
 */
export function migrateManagedTableStorage(storageKey: string, accountId: string | null): void {
  if (typeof window === "undefined") return;
  try {
    const columnsKey = `managedTable.columns.${storageKey}`;
    const columns = readStorage(columnsKey);
    if (columns != null) {
      const target = `settings.${storageKey}-columns`;
      if (readStorage(target) == null) window.localStorage.setItem(target, columns);
      window.localStorage.removeItem(columnsKey);
    }
    const sortKey = `managedTable.sort.v2.${storageKey}`;
    const sort = readStorage(sortKey);
    if (sort != null) {
      const target = `proTable.sort.${(accountId || "anon").trim() || "anon"}.settings.${storageKey}`;
      const parsed = JSON.parse(sort) as { field?: unknown; order?: unknown } | null;
      if (readStorage(target) == null && parsed && typeof parsed.field === "string" && (parsed.order === SORT_ASCEND || parsed.order === SORT_DESCEND)) {
        window.localStorage.setItem(target, JSON.stringify({ field: parsed.field, order: parsed.order }));
      }
      window.localStorage.removeItem(sortKey);
    }
  } catch {
    // Browserspeicher nicht verfügbar oder Altwert unlesbar: die Liste startet mit Standardeinstellungen.
  }
}

/**
 * Verwaltete Liste über die gemeinsame Listenseite (15.10.2026): dieselben Zeilen, Spaltenverwaltung,
 * Sortierung, Aktionen und Kopfform wie jede andere Liste. Die Props der früheren Tabellenkarte bleiben.
 */
export function ManagedSettingsList<T extends object>({
  storageKey,
  columns: sourceColumns,
  dataSource,
  tr,
  rowKey = "id",
  loading = false,
  pagination,
  paginationSummary,
  onSortChange,
  emptyText,
  expandable,
  defaultSort,
  tableProps,
  activeRowKey,
  rowReorder,
  rowDrag,
  rowOverlay,
  summary,
  actions,
  actionColumnKey = DEFAULT_ACTION_COLUMN_KEY,
  className,
  toolbarExtra,
  frame,
}: ManagedTableListProps<T> & { frame: ManagedTableFrame }) {
  const baseRuntime = useResolvedSettingsListRuntime();
  const lang = useUiLanguage();
  // Spaltentitel und Texte der Liste kommen wie bisher über `tr`; Shared-Texte gehen vor.
  const runtime = useMemo<SettingsListRuntime>(() => ({
    ...baseRuntime,
    text: (value: LocalizedText) => {
      if (typeof value !== "string") return lang === LANGUAGE_DE ? value.de : value.en;
      return (lang === LANGUAGE_DE ? sharedSettingsListDe : sharedSettingsListEn)[value] ?? tr(value);
    },
  }), [baseRuntime, lang, tr]);
  useState(() => {
    migrateManagedTableStorage(storageKey, baseRuntime.accountId);
    return true;
  });

  const mapped = useMemo(() => {
    const columns = withKeys(sourceColumns).filter((column) => !column.hidden);
    const isActionColumn = (column: ManagedTableColumn<T>) =>
      column.key === actionColumnKey || column.key === ROW_ACTION_COLUMN_KEY || column.pinned === "right";
    const cellRender = (column: ManagedTableColumn<T>) => column.render
      ? (_value: unknown, record: T, index: number): ReactNode => {
          const value = column.render?.(valueAt(record, column.dataIndex), record, index);
          // Antd-Zellform `{ children, props }`: nur der Inhalt wird gezeigt.
          if (value != null && typeof value === "object" && !isValidElement(value) && ("children" in value || "props" in value)) {
            return (value as { children?: ReactNode }).children;
          }
          return expandSettingsListRenderers(value as ReactNode);
        }
      : undefined;
    const dataColumns = columns.filter((column) => !isActionColumn(column));
    // Alle rechts fixierten bzw. Aktionsspalten bilden zusammen die eine Aktionsspalte der Liste.
    const actionColumns = columns.filter(isActionColumn);
    const allColumnsMap: Record<string, ProColumnsWithMeasure<T>> = {};
    for (const column of dataColumns) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Verwaltungsfelder gehören nicht in die Listenspalte.
      const { pinned, hidden, sortable, defaultVisible, sortValue, sorter, render, title, ...rest } = column;
      const isDefaultSort = defaultSort != null && (defaultSort.field === column.key || defaultSort.field === sortFieldOf(column));
      allColumnsMap[column.key] = {
        ...(rest as ProColumnsWithMeasure<T>),
        key: column.key,
        title: typeof title === "function" ? column.key : title,
        dataIndex: column.dataIndex,
        sorter,
        sortValue,
        defaultSortOrder: isDefaultSort ? defaultSort.order : undefined,
        render: cellRender(column),
      };
    }
    const columnDefinitions: ColumnDefinition[] = dataColumns.map((column) => ({
      key: column.key,
      label: typeof column.title === "string" ? column.title : undefined,
      defaultVisible: column.defaultVisible,
    }));
    const actionRenders = actionColumns.map(cellRender).filter((render) => render != null);
    const actionsColumn = actions || actionColumns.length > 0
      ? {
          key: actionColumns.find((column) => column.key === actionColumnKey)?.key ?? actionColumnKey,
          title: "",
          valueType: "option" as const,
          render: actions ? undefined : (value: unknown, record: T, index: number) =>
            actionRenders.flatMap((render) => {
              const node = render(value, record, index);
              return Array.isArray(node) ? node : node == null || node === false ? [] : [node];
            }),
        }
      : undefined;
    return { allColumnsMap, columnDefinitions, actionsColumn };
  }, [actionColumnKey, actions, defaultSort, sourceColumns]);

  const rowKeyOf = useCallback((record: T) => {
    if (typeof rowKey === "function") return String(rowKey(record, dataSource.indexOf(record)));
    return String(valueAt(record, typeof rowKey === "string" ? rowKey : "id") ?? dataSource.indexOf(record));
  }, [dataSource, rowKey]);

  const onRow = tableProps?.onRow;
  // Einfacher Klick öffnet die Zeile; ein übergebener Doppelklick gilt als Klick (kein Doppelklick mehr).
  const rowHandler = (record: T) => {
    const handlers = onRow?.(record, dataSource.indexOf(record));
    const handler = handlers?.onClick ?? handlers?.onDoubleClick;
    if (handler) handler({} as never);
  };
  const tableRowClassName = tableProps?.rowClassName;
  const rowSelection = tableProps?.rowSelection;
  const controlledExpansion = expandable?.expandedRowKeys != null;
  const renderExpanded = expandable?.expandedRowRender
    ? (record: T) => (expandable.expandedRowRender?.(record, dataSource.indexOf(record), 0, true) as ReactNode) ?? null
    : undefined;

  const listPagination: SettingsListPageProps<T>["pagination"] = pagination === false
    ? false
    : {
        pageSize: (typeof pagination === "object" && pagination?.pageSize) || DEFAULT_PAGE_SIZE,
        showTotal: typeof pagination === "object" ? pagination?.showTotal : undefined,
        current: typeof pagination === "object" && pagination?.total != null ? pagination.current : undefined,
        total: typeof pagination === "object" && pagination?.current != null ? pagination.total : undefined,
        onChange: typeof pagination === "object" ? (pagination?.onChange as ((page: number, size: number) => void) | undefined) : undefined,
      };

  const titleNode = frame.sectionTitle;
  return (
    <SettingsListPage<T>
      runtime={runtime}
      storageKey={storageKey}
      rowKey={rowKeyOf}
      dataSource={dataSource}
      loading={loading}
      columnDefinitions={mapped.columnDefinitions}
      allColumnsMap={mapped.allColumnsMap}
      actions={actions}
      actionsColumn={mapped.actionsColumn}
      embedded={frame.embedded}
      sectionTitle={titleNode}
      toolbarExtra={toolbarExtra}
      toolbarRightExtra={frame.headerExtra}
      toolbarRightAlign
      beforeTable={frame.beforeTable}
      afterTable={frame.footer}
      emptyText={emptyText}
      pagination={listPagination}
      paginationSummary={paginationSummary}
      onRequestSortChange={onSortChange ? (sort) => {
        const entry = Object.entries(sort ?? {})[0];
        onSortChange(entry ? { field: entry[0], order: entry[1] } : null);
      } : undefined}
      summary={summary}
      tableClassName={className}
      expandedRowRender={controlledExpansion ? renderExpanded : undefined}
      expandedRowKeys={controlledExpansion ? [...(expandable?.expandedRowKeys ?? [])] : undefined}
      expandable={!controlledExpansion && renderExpanded ? {
        render: renderExpanded,
        rowExpandable: expandable?.rowExpandable ? (record) => Boolean(expandable.rowExpandable?.(record)) : undefined,
      } : undefined}
      rowSelection={rowSelection ? {
        selectedRowKeys: rowSelection.selectedRowKeys ? [...rowSelection.selectedRowKeys] : undefined,
        onChange: rowSelection.onChange ? (keys, rows) => rowSelection.onChange?.(keys, rows, { type: "multiple" }) : undefined,
        getCheckboxProps: rowSelection.getCheckboxProps
          ? (record) => {
              const props = rowSelection.getCheckboxProps?.(record);
              return { disabled: props?.disabled, title: typeof props?.title === "string" ? props.title : undefined };
            }
          : undefined,
        hideSelectAll: rowSelection.hideSelectAll,
      } : undefined}
      activeRowKey={activeRowKey}
      rowReorder={rowReorder}
      rowDrag={rowDrag}
      rowOverlay={rowOverlay}
      onRowClick={onRow ? rowHandler : undefined}
      rowClassName={tableRowClassName ? (record) => (typeof tableRowClassName === "function" ? tableRowClassName(record, dataSource.indexOf(record), 0) : String(tableRowClassName)) : undefined}
    />
  );
}

/**
 * Gemeinsame Liste ohne eigene Karte (14.10.2026): ersetzt die rohe Antd-`Table` innerhalb einer bestehenden
 * Karte. Rendert seit 15.10.2026 über die gemeinsame Listenseite. Mit eigener Karte: `ManagedTableCard`.
 */
export function ManagedTableList<T extends object>(props: ManagedTableListProps<T>) {
  return <ManagedSettingsList<T> {...props} frame={{ embedded: true }} />;
}
